
import React from 'react';
import Logo from './Logo';
import Footer from './Footer';
import { Button } from './ui/button';
import { ChevronLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { TooltipProvider } from "@/components/ui/tooltip";
import ThemeToggle from './ThemeToggle';
import Navigation from './Navigation';
import MobileNavigation from './MobileNavigation';
import SearchTrigger from './SearchTrigger';

interface LayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
}

const Layout = React.memo(({ children, showBackButton = false }: LayoutProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const getBackLink = () => {
    if (isHomePage) return '/';
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 3 && pathSegments[0] === 'calculators') {
      return `/calculators/${pathSegments[1]}`;
    }
    return '/';
  };

  const backLink = getBackLink();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Sticky header with subtle bottom border */}
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
            {/* Left: Logo + back button + nav */}
            <div className="flex items-center gap-3">
              {showBackButton && !isHomePage && (
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="gap-1.5 text-muted-foreground hover:text-foreground animate-fade-in"
                >
                  <Link to={backLink} aria-label="Go back">
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Back</span>
                  </Link>
                </Button>
              )}
              <Logo />
              <div className="hidden md:block ml-2">
                <Navigation />
              </div>
            </div>

            {/* Right: search, theme, mobile menu */}
            <div className="flex items-center gap-2">
              <SearchTrigger />
              <ThemeToggle />
              <MobileNavigation />
            </div>
          </div>
        </header>

        <main className="animate-fade-in" key={location.pathname}>
          {children}
        </main>

        <Footer />
      </div>
    </TooltipProvider>
  );
});

Layout.displayName = 'Layout';

export default Layout;
